/**
 * MC Collection Exhibition Rail — arrows, progress, optional center emphasis.
 * Native scroll + scroll-snap remain primary; JS is progressive enhancement.
 */
(function () {
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initRail(root) {
    if (!root) return;

    if (root._mcRailAbort) {
      root._mcRailAbort.abort();
    }

    var track = root.querySelector('[data-mc-rail-track]');
    if (!track) return;

    var prev = root.querySelector('[data-mc-rail-prev]');
    var next = root.querySelector('[data-mc-rail-next]');
    var progressBar = root.querySelector('[data-mc-rail-progress-bar]');
    var statusEl = root.querySelector('[data-mc-rail-status]');
    var enableEmphasis = root.hasAttribute('data-mc-rail-emphasis');
    var total = parseInt(root.getAttribute('data-mc-rail-count') || '0', 10);

    var abort = new AbortController();
    var signal = abort.signal;
    root._mcRailAbort = abort;
    root.dataset.mcRailReady = 'true';

    var scrolling = false;
    var scrollEndTimer = null;
    var cards = function () {
      return track.querySelectorAll('[data-mc-exh-card]');
    };

    function cardStep() {
      var card = track.querySelector('[data-mc-exh-card]');
      if (!card) return track.clientWidth * 0.8;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      var step = card.getBoundingClientRect().width + gap;
      // On wider screens, advance by one card (peek invites the next)
      return step;
    }

    function updateArrows() {
      if (!prev && !next) return;
      var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 1);
      var left = track.scrollLeft;
      if (prev) {
        var atStart = left <= 1;
        prev.disabled = atStart;
        prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (next) {
        var atEnd = left >= maxScroll;
        next.disabled = atEnd;
        next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
    }

    function updateProgress() {
      var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      var ratio = maxScroll <= 0 ? 1 : Math.min(1, Math.max(0, track.scrollLeft / maxScroll));
      if (progressBar) {
        // Keep a visible minimum so the bar never disappears at start
        var scale = Math.max(0.12, 0.12 + ratio * 0.88);
        progressBar.style.transform = 'scaleX(' + scale + ')';
      }
      if (statusEl && total > 0) {
        var step = cardStep();
        var index = step > 0 ? Math.round(track.scrollLeft / step) + 1 : 1;
        index = Math.min(total, Math.max(1, index));
        var pad = function (n) {
          return n < 10 ? '0' + n : String(n);
        };
        statusEl.textContent = pad(index) + ' / ' + pad(total);
      }
    }

    function updateEmphasis() {
      if (!enableEmphasis || prefersReducedMotion()) return;
      var list = cards();
      if (!list.length) return;
      var trackRect = track.getBoundingClientRect();
      var centerX = trackRect.left + trackRect.width / 2;
      var best = null;
      var bestDist = Infinity;
      list.forEach(function (card) {
        var rect = card.getBoundingClientRect();
        var cardCenter = rect.left + rect.width / 2;
        var dist = Math.abs(cardCenter - centerX);
        if (dist < bestDist) {
          bestDist = dist;
          best = card;
        }
      });
      list.forEach(function (card) {
        card.classList.toggle('is-centered', card === best);
      });
    }

    function onScroll() {
      updateArrows();
      updateProgress();
      updateEmphasis();
    }

    function scrollByDir(dir) {
      if (scrolling) return;
      if ((dir < 0 && prev && prev.disabled) || (dir > 0 && next && next.disabled)) {
        return;
      }
      scrolling = true;
      track.scrollBy({
        left: cardStep() * dir,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(
        function () {
          scrolling = false;
          onScroll();
        },
        prefersReducedMotion() ? 50 : 420
      );
    }

    if (prev) {
      prev.addEventListener(
        'click',
        function () {
          scrollByDir(-1);
        },
        { signal: signal }
      );
    }
    if (next) {
      next.addEventListener(
        'click',
        function () {
          scrollByDir(1);
        },
        { signal: signal }
      );
    }

    track.addEventListener('scroll', onScroll, { passive: true, signal: signal });

    track.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollByDir(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollByDir(1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          track.scrollTo({
            left: 0,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        } else if (event.key === 'End') {
          event.preventDefault();
          track.scrollTo({
            left: track.scrollWidth,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        }
      },
      { signal: signal }
    );

    window.addEventListener('resize', onScroll, { signal: signal });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(onScroll);
      ro.observe(track);
      signal.addEventListener('abort', function () {
        ro.disconnect();
      });
    }

    onScroll();
  }

  function initAll() {
    document.querySelectorAll('[data-mc-collection-rail]').forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    event.target.querySelectorAll('[data-mc-collection-rail]').forEach(initRail);
  });

  document.addEventListener('shopify:section:reorder', initAll);
})();
